import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { AsyncPanel } from "../ui/AsyncPanel.js";
import { useSession } from "./AppProviders.js";

/**
 * Route guard for everything behind sign-in.
 *
 * - No session → `/login?next=<intended path>` so the person lands back where
 *   they were heading. Only same-origin paths survive the round trip
 *   ({@link safeReturnPath}); anything that could leave the site is dropped.
 * - A session that still carries a temporary password → `/change-password`
 *   for every route except the password page itself.
 * - Session unknown (server unreachable) → the offline panel with a retry.
 */

export const LOGIN_PATH = "/login";
export const CHANGE_PASSWORD_PATH = "/change-password";
/** Query parameter carrying the intended destination through the auth pages. */
export const RETURN_PARAM = "next";

const SAME_ORIGIN_BASE = "http://same-origin.invalid";

/**
 * Returns `candidate` as an in-app path (`/path?query#hash`) or `null` when it
 * is empty, relative, points at another origin (absolute URL, `//host`,
 * backslash tricks), or targets one of the auth pages themselves.
 */
export function safeReturnPath(
  candidate: string | null | undefined,
): string | null {
  if (typeof candidate !== "string" || candidate.length === 0) return null;
  if (!candidate.startsWith("/")) return null;
  if (/^\/[\\/]/.test(candidate)) return null;

  let url: URL;
  try {
    url = new URL(candidate, SAME_ORIGIN_BASE);
  } catch {
    return null;
  }
  if (url.origin !== SAME_ORIGIN_BASE) return null;

  const path = url.pathname + url.search + url.hash;
  if (url.pathname === LOGIN_PATH || url.pathname === CHANGE_PASSWORD_PATH)
    return null;
  return path;
}

/** The login URL that brings the person back to `location` afterwards. */
export function loginPathFor(location: {
  pathname: string;
  search: string;
}): string {
  return withReturnPath(LOGIN_PATH, location.pathname + location.search);
}

/** Appends `?next=` to `basePath` when `candidate` is a safe, non-root destination. */
export function withReturnPath(
  basePath: string,
  candidate: string | null | undefined,
): string {
  const next = safeReturnPath(candidate);
  if (!next || next === "/") return basePath;
  return `${basePath}?${RETURN_PARAM}=${encodeURIComponent(next)}`;
}

export interface RequireSessionProps {
  /** Rendered when the guard passes; defaults to the nested route `<Outlet />`. */
  children?: ReactNode;
  /** Let a session that must still replace its temporary password through (the password page). */
  allowTemporaryPassword?: boolean;
}

export function RequireSession({
  children,
  allowTemporaryPassword = false,
}: RequireSessionProps) {
  const { user, status, error, refresh } = useSession();
  const location = useLocation();

  if (status === "loading") {
    return (
      <main className="page-status">
        <AsyncPanel isLoading loadingLabel="Checking your session…">
          {null}
        </AsyncPanel>
      </main>
    );
  }

  if (status === "unavailable") {
    return (
      <main className="page-status">
        <AsyncPanel
          error={error}
          onRetry={() => {
            void refresh().catch(() => undefined);
          }}
        >
          {null}
        </AsyncPanel>
      </main>
    );
  }

  if (!user) {
    return <Navigate to={loginPathFor(location)} replace />;
  }

  if (user.mustChangePassword && !allowTemporaryPassword) {
    return (
      <Navigate
        to={withReturnPath(
          CHANGE_PASSWORD_PATH,
          location.pathname + location.search,
        )}
        replace
      />
    );
  }

  return children ?? <Outlet />;
}
